import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import U2000BasicDialog from "../../../../../../u2000Core/components/dialog/U2000BasicDialog";
import U3125WipTransactionTable from "../../../../../../u3125Facture/components/form/u3125Facture/U3125WipTransactionTable";
import U3121BillingSummaryDetailDlgBody from "./U3121BillingSummaryDetailDlgBody";
import { NS } from "../../../../../U3121";
import type { U3121BillingTecPool } from "../../../../../U3121Service";
import type { U3134WorkInProgressSearchRow } from "../../../../../../u3134WorkInProgress/U3134Service";
import { useU3121BillingSummaryTecDetailHandlers } from "../../../../../U3121UseBillingSummaryHandlers";

export interface U3121BillingSummaryTecDetailDlgProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  pool: U3121BillingTecPool;
}

function poolTitleKey(pool: U3121BillingTecPool): string {
  if (pool === "invoiced") return "dlgBillingSummaryTecInvoicedTitle";
  if (pool === "writeOff") return "dlgBillingSummaryTecWriteOffTitle";
  return "dlgBillingSummaryTecUninvoicedTitle";
}

const U3121BillingSummaryTecDetailDlg: React.FC<U3121BillingSummaryTecDetailDlgProps> = ({ isOpen, onClose, projectId, pool }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const detailHandlers = useU3121BillingSummaryTecDetailHandlers();
  const [lines, setLines] = useState<U3134WorkInProgressSearchRow[]>([]);

  useEffect(() => {
    if (!isOpen || !projectId.trim()) return;
    let cancelled = false;
    setLoading(true);
    void (async () => {
      const rows = await detailHandlers.fetch(projectId, pool);
      if (cancelled) return;
      setLines(rows);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, projectId, pool, detailHandlers]);

  const title = useMemo(() => t(poolTitleKey(pool), { ns: NS }), [pool, t]);

  return (
    <U2000BasicDialog isOpen={isOpen} onClose={() => onClose()} title={title} width="w-[60vw]" height="max-h-[60vh]">
      <U3121BillingSummaryDetailDlgBody onClose={onClose}>
        {loading ? (
          <p className="text-sm text-gray-600">{t("msgLoading", { ns: NS })}</p>
        ) : lines.length === 0 ? (
          <p className="text-sm text-gray-600">{t("msgBillingSummaryDetailEmpty", { ns: NS })}</p>
        ) : (
          <U3125WipTransactionTable
            rows={lines}
            variant="full"
            translationNs={NS}
            scrollMaxHeightClass="min-h-0 max-h-full flex-1"
          />
        )}
      </U3121BillingSummaryDetailDlgBody>
    </U2000BasicDialog>
  );
};

export default U3121BillingSummaryTecDetailDlg;
